/**
 * @file OrderCancelModal.tsx
 *
 * Cancel-order dialog: an optional reason on Gravity's {@link StandardDialog}. The
 * parent supplies the confirm handler (which calls the store's `cancelOrder`) and the
 * saving / error state. Unlike the free-form status edit (the no-money
 * `OrderStatusEditModal` backdoor), cancelling here is the real money path: the engine
 * voids outstanding authorizations and refunds captured funds.
 */

import { useId, useState } from 'react';
import { StandardDialog } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';

interface OrderCancelModalProps {
  saving: boolean;
  /** Optional error shown in-dialog when the cancel fails. */
  errorMessage?: string;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

/**
 * Renders the cancel-order dialog.
 *
 * @param props - The loading/error state and the confirm/close handlers.
 *
 * @returns The dialog element.
 */
export function OrderCancelModal({
  saving,
  errorMessage,
  onConfirm,
  onClose,
}: OrderCancelModalProps) {
  const intl = useIntl();
  const reasonId = useId();
  const [reason, setReason] = useState('');

  const handleDismiss = (): void => {
    if (saving) {
      return;
    }
    onClose();
  };

  return (
    <StandardDialog
      isOpen
      title={intl.formatMessage(adminMessages.ordersCancelTitle)}
      onClose={handleDismiss}
      onClickAway={handleDismiss}
      onEscape={handleDismiss}
      footer={
        <div className="gv-button-group">
          <button
            className="gv-button gv-button-cancel"
            type="button"
            data-testid="order-cancel-dismiss"
            onClick={handleDismiss}
            disabled={saving}
          >
            {intl.formatMessage(adminMessages.ordersCancelDismiss)}
          </button>
          <button
            className="gv-button gv-button-primary"
            type="button"
            data-testid="order-cancel-confirm"
            onClick={() => onConfirm(reason)}
            disabled={saving}
          >
            {saving
              ? intl.formatMessage(adminMessages.ordersCancelLoading)
              : intl.formatMessage(adminMessages.ordersCancelConfirm)}
          </button>
        </div>
      }
    >
      <div className="gv-form-option gv-w-full" data-testid="order-cancel-modal">
        <label className="gv-label" htmlFor={reasonId}>
          {intl.formatMessage(adminMessages.ordersCancelReasonLabel)}
        </label>
        <textarea
          id={reasonId}
          className="gv-input gv-input-textarea gv-w-full"
          rows={3}
          data-testid="order-cancel-reason"
          value={reason}
          placeholder={intl.formatMessage(adminMessages.ordersCancelReasonPlaceholder)}
          onChange={(event) => setReason(event.target.value)}
        />
        {errorMessage && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="order-cancel-error"
            style={{ marginBottom: 0 }}
          >
            {errorMessage}
          </p>
        )}
      </div>
    </StandardDialog>
  );
}
